var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { useAttachmentClient, useFunctionClient, useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import React from 'react';
import { fileEntityName, fileVersionEntityName } from '../../backend/features/file-manager';
export function useDocumentLibraryStorage() {
    const fileStorageClient = useStorageClient(fileEntityName);
    const functionClient = useFunctionClient();
    const attachmentClient = useAttachmentClient();
    const updateFilePermissions = React.useCallback((request) => functionClient.executeFunction({ functionName: 'updateFilePermissions', params: request }).then((response) => response.result), [functionClient]);
    const listFiles = React.useCallback((request) => functionClient
        .executeFunction({ functionName: 'listFiles', params: request })
        .then((response) => response.result), [functionClient]);
    const listFileParents = React.useCallback((request) => functionClient
        .executeFunction({ functionName: 'listFileParents', params: request })
        .then((response) => response.result), [functionClient]);
    const isFileExists = React.useCallback((request) => functionClient
        .executeFunction({
        functionName: 'isFileExists',
        params: request,
    })
        .then((response) => response.result), [functionClient]);
    const checkIn = React.useCallback((request, progressCallback) => __awaiter(this, void 0, void 0, function* () {
        // create version entry
        const checkinRequest = {
            fileId: request.fileId,
            comment: request.comment,
            contentLength: request.contentLength,
        };
        const checkinResponse = yield functionClient.executeFunction({
            functionName: 'checkIn',
            params: checkinRequest,
        });
        const { id: fileVersionId } = checkinResponse.result;
        try {
            // attach content to version
            yield attachmentClient.post({
                entity: fileVersionEntityName,
                entityId: fileVersionId,
                key: 'data',
                contentType: request.contentType ? request.contentType : 'application/octet-stream',
                contentLength: request.contentLength,
                content: request.content,
            }, progressCallback);
            return checkinResponse.result;
        }
        catch (e) {
            handleErrorDialog(e);
        }
    }), [functionClient, attachmentClient]);
    const copyFile = React.useCallback((request) => functionClient.executeFunction({ functionName: 'copyFile', params: request }).then((response) => response.result), [functionClient]);
    const createDirectory = React.useCallback((request) => {
        var _a;
        return fileStorageClient.post({
            action: 'create',
            data: Object.assign(Object.assign({}, ((_a = request.additionalData) !== null && _a !== void 0 ? _a : {})), { name: request.name, directory: true, type: 'directory', ownerId: request.ownerId, versionCount: 0, archived: false }),
            parentId: request.parentId,
        });
    }, [fileStorageClient]);
    const createFile = React.useCallback((request, progressCallback) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // create the file "record", file will be created in checked out state
        const createFileResponse = yield fileStorageClient.post({
            action: 'create',
            data: Object.assign(Object.assign({}, ((_a = request.additionalData) !== null && _a !== void 0 ? _a : {})), { name: request.name, directory: false, type: request.contentType ? request.contentType : 'application/octet-stream', contentLength: request.contentLength, ownerId: request.ownerId, checkOutUserId: request.ownerId, versionCount: 0, archived: false }),
            parentId: request.parentId,
        });
        // checkin the file contents
        yield checkIn({
            fileId: createFileResponse.id,
            content: request.content,
            contentType: request.contentType,
            contentLength: request.contentLength,
        }, progressCallback);
        return createFileResponse;
    }), [checkIn, fileStorageClient]);
    const listFileVersions = React.useCallback((request) => functionClient
        .executeFunction({ functionName: 'listFileVersions', params: request })
        .then((response) => response.result), [functionClient]);
    const revertToVersion = React.useCallback((request) => functionClient
        .executeFunction({ functionName: 'revertToVersion', params: request })
        .then((response) => response.result), [functionClient]);
    const watchFile = React.useCallback((request) => functionClient.executeFunction({ functionName: 'watchFile', params: request }).then((response) => response.result), [functionClient]);
    const unwatchFile = React.useCallback((request) => functionClient.executeFunction({ functionName: 'unwatchFile', params: request }).then((response) => response.result), [functionClient]);
    return React.useMemo(() => ({
        fileStorageClient: fileStorageClient,
        createDirectory: createDirectory,
        createFile: createFile,
        copyFile: copyFile,
        listFiles: listFiles,
        listFileParents: listFileParents,
        isFileExists: isFileExists,
        checkIn: checkIn,
        listFileVersions: listFileVersions,
        revertToVersion: revertToVersion,
        watchFile: watchFile,
        unwatchFile: unwatchFile,
        updateFilePermissions: updateFilePermissions,
    }), [
        fileStorageClient,
        createDirectory,
        createFile,
        copyFile,
        listFiles,
        listFileParents,
        isFileExists,
        checkIn,
        listFileVersions,
        revertToVersion,
        watchFile,
        unwatchFile,
        updateFilePermissions,
    ]);
}
